<template>
  <VueSimpleSuggest
      ref="autoSuggest"
      display-attribute="fallback"
      value-attribute="value"
      :list="fetchSearchResults"
      :debounce="500"
      :destyled="true"
      :styles="searchStyles"
      :max-suggestions="100"
      :min-length="2"
      @select="selectItem"
      @hover="checkIfHasToScroll"
  >
    <div class="search-input-container">
      <i class="search-icon"></i>
      <input v-model="term" type="text" :placeholder="placeholder" spellcheck="false"
             class="search-input">
      <i v-if="isLoading" class="loader-icon"></i>
    </div>
    <template slot="suggestion-item" slot-scope="{ suggestion, query }">
      <div :class="`${suggestion.imgClass}-div`">
        <div :class="`${suggestion.imgClass}-div-inner`">
          <img loading="lazy" :alt="suggestion.fallback" :src="suggestion.imgUrl" :class="suggestion.imgClass"
               v-if="suggestion.imgUrl">
        </div>
      </div>
      <div class="ac-details-div">
        <div v-if="suggestion.node_name" class="ac-node-name robotoC" v-html="escape(suggestion.node_name)"/>
        <div v-else-if="suggestion.count && suggestion.count !== '1'" class="ac-node-name robotoC"
             v-html="escape(suggestion.label)"/>
        <div v-else class="ac-node-name robotoC">{{ suggestion.label }}</div>
        <div v-if="suggestion.disambiguator" class="ac-disambiguator robotoL">{{ suggestion.disambiguator }}</div>
      </div>
    </template>

    <template slot="misc-item-above" slot-scope="{ suggestions, query }" v-if="!isLoading">
      <li class="ac-li ui-menu-item" v-if="!suggestions.length">
        <div class="ac-node-image-div">
          <div class="ac-node-image-div-inner">
            <img width="50" height="50" alt="No results" :src="`${getInsightsUrl()}/images/noresults_55x55.png`"
                 class="ac-node-image">
          </div>
        </div>
        <div class="ac-details-div">
          <div class="ac-node-name robotoC">No Search Results</div>
          <div class="ac-disambiguator robotoL"></div>
        </div>
      </li>
    </template>
  </VueSimpleSuggest>
</template>

<script>
import VueSimpleSuggest from 'vue-simple-suggest';
import escape from 'lodash/escape';
import {searchItems} from '@/api/search';

const audienceIcon = require('@/assets/images/audience-icon.png');

const countRegexp = /([0-9]+(\.[0-9]+)?[KM])/i;
const countRegexp2 = /M$/i;
const countRegexp3 = /[^0-9.]+/g;

export default {
  name: 'SearchInput',
  components: {
    VueSimpleSuggest
  },
  props: {
    placeholder: {
      type: String,
      default: 'Search'
    }
  },
  data() {
    return {
      term: '',
      selectedTerm: null,
      searchStyles: {
        suggestions: 'ui-autocomplete ui-front ui-menu ui-widget ui-widget-content',
        suggestItem: 'ac-li ui-menu-item'
      },
      isLoading: false
    };
  },
  methods: {
    getInsightsUrl() {
      return process.env.INSIGHTS_V1_HOST;
    },
    escape,
    selectItem(item) {
      const queryObj = {
        adjectives: 'any',
        classid: 0,
        audience: '',
        list_id: '',
        id: ''
      };
      if (item.audience || item.list) {
        queryObj.interstitial = 1;
      }
      else if (item.count) {
        const matches = item.count.match(countRegexp);
        if (matches && (countRegexp2.test(matches[1]) || parseFloat(matches[1].replace(countRegexp3, '')) >= 1/*its in thousands*/)) {
          queryObj.interstitial = 1;
        }
      }

      if (item.audience) {
        queryObj.audience = item.value;
      }
      else if (item.list) {
        queryObj.list_id = item.value;
      }
      else {
        queryObj.id = item.value;
      }
      let queryString = Object.entries(queryObj)
          .filter(([, value]) => typeof value !== 'undefined' && value !== null && value !== 'null')
          .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
          .join('&');

      window.open(`${process.env.INSIGHTS_V1_HOST}/?${queryString}`, '_blank');
      this.$emit('selected', item);
    },
    async fetchSearchResults() {
      try {
        const term = this.term.trim().toLowerCase();
        if (!term) {
          return;
        }
        this.isLoading = true;
        const data = await searchItems({
          type: 'search',
          correlations: 1,
          term,
          class_ids: 0,
          list: 0
        });
        if (!data) {
          return [];
        }
        this.isLoading = false;
        return data.map(this.addRenderProps);
      }
      catch (e) {
        this.isLoading = false;
        return [];
      }
    },
    addRenderProps(item) {
      let imgServer = 1;
      let imgFolder = '';
      let imgClass = 'ac-node-image';
      let imgUrl,
          bucket,
          fallback;
      if (item.image_name && item.image_name.length || item.image_url) {
        if (item.image_name && item.image_name.length) {
          imgServer = (++imgServer > 3 ? 1 : imgServer);

          if (item.list) {
            bucket = parseInt(item.value) % 20000;
            imgUrl = `//imgix.ranker.com/list_img/${bucket}/${item.value}/original/${item.image_name}?w=100&h=100&q=90&fm=jpg&fit=crop&crop=faces`;
            imgClass = 'ac-node-image';
          }
          else if (item.audience) {
            imgUrl = audienceIcon;
            imgClass = 'ac-audience-image';
          }
          else {
            imgFolder = (item.image_is_user ? 'user_' : '') + 'node_img/';
            imgFolder += (Math.floor(item.value / 20000) + 1) + '/' + item.value + '/original/';
            imgUrl = `//imgix.ranker.com/${imgFolder}${item.image_name}?w=100&h=100&q=90&fm=jpg&fit=crop&crop=faces`;
            imgClass = 'ac-node-image';
          }
        }
        else {
          imgClass = 'ac-node-image';
          imgUrl = window.location.protocol + '//' + window.location.hostname + `/${item.image_url}`;
        }
      }
      fallback = item.node_name || item.label || item.value;
      return {
        ...item,
        imgServer,
        imgUrl,
        imgClass,
        imgFolder,
        bucket,
        fallback
      };
    },
    checkIfHasToScroll() {
      /*const {$el} = this.$refs.autoSuggest;
      const selected = $el.querySelector('.suggest-item[aria-selected="true"]');
      const container = $el.querySelector('.suggestions');
      if(selected) {
        // TODO scroll selected element into view
      }*/
    }
  }
};
</script>

<style lang="postcss">
.vue-simple-suggest {
  @apply relative;

  &.small {
    .search-icon {
      left: 13px;
    }
    .search-input {
      height: 36px;
      max-width: 300px;
      border-radius: 24px;
      padding: 0 35px;
      font-size: 17px;
      line-height: 20px;
      text-align: left;
    }
    .loader-icon {
      right: 13px;
    }
    .ui-autocomplete {
      top: 38px;
      left: 16px;
      right: 16px;
    }
  }

  .search-input-container {
    @apply relative;
  }

  .search-icon {
    background-image: url('~@/assets/icons/search.png');
    background-repeat: no-repeat;
    background-size: 100% 18px;
    background-position: center;
    width: 18px;
    height: 100%;
    left: 26px;
    top: -50%;
    transform: translateY(50%);
    @apply absolute;
  }

  .loader-icon {
    background-image: url('~@/assets/images/ajax-spinner-small.gif');
    background-repeat: no-repeat;
    background-size: 100% 16px;
    background-position: center;
    width: 16px;
    height: 100%;
    right: 26px;
    top: -50%;
    transform: translateY(50%);
    @apply absolute;
  }

  .search-input {
    font-family: Roboto, sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 20px;
    line-height: 23px;
    text-align: center;
    letter-spacing: -0.01em;
    background-color: #FFFFFF;
    border-radius: 30px;
    height: 52px;
    padding: 0 60px;
    @apply outline-none w-full;
  }

  .ui-widget-content {
    border: 1px solid #aaaaaa;
    background: #ffffff;
    color: #222222;
  }

  .ui-widget {
    font-family: Verdana, Arial, sans-serif;
    font-size: 1.1em;
  }

  .ui-menu {
    list-style: none;
    padding: 0;
    margin: 0;
    display: block;
    outline: none;
  }

  .ui-autocomplete {
    position: absolute;
    top: 55px;
    left: 20px;
    right: 20px;
    cursor: default;
  }

  .ui-autocomplete .ui-menu-item {
    list-style-image: none !important;
  }

  .ui-autocomplete {
    z-index: 1061 !important;
    max-height: 500px;
    overflow-y: auto;
    /* prevent horizontal scrollbar */
    overflow-x: hidden;
  }

  /* IE 6 doesn't support max-height
     * we use height instead, but this forces the menu to always be this tall
     */

  * html .ui-autocomplete {
    height: 500px;
  }

  .ac-node-image {
    width: 50px;
    height: 50px;
  }

  .ac-audience-image {
    width: 54px;
    height: 54px;
  }

  .ac-count {
    color: grey;
  }

  .ac-node-name {
    font-size: 15px;
    color: black;
    margin-left: 7px;
  }

  .ac-disambiguator {
    font-size: 11px;
    color: #6b6b6b;
    margin-left: 7px;
    white-space: normal;
  }

  .ac-node-image-div, .ac-audience-image-div {
    display: table-cell;
    vertical-align: middle;
    width: 60px;
    height: 60px;
  }

  .ac-node-image-div-inner, .ac-audience-image-div-inner {
    margin: 5px;
    border-width: 1px;
    border-color: #cfcfcf;
    border-style: solid;
    width: 56px;
    height: 56px;
    padding: 2px;
  }

  .ac-audience-image-div-inner {
    padding: 0;
  }

  .ac-details-div {
    display: table-cell;
    /*display: inline-block;*/
    max-width: 295px;
    vertical-align: middle;
  }

  .ui-menu .ui-menu-item.ac-li {
    padding: 0;
  }

  .ac-li {
    min-height: 55px !important;
    border-bottom: 1px solid rgb(216, 216, 218);
    /*clear: both;*/
  }

  .ui-menu .ui-menu-item.ac-li {
    &:hover {
      cursor: pointer;
    }

    &.hover,
    &:hover,
    &:focus {

      /*height: 53px;
      margin-top: -1px !important;
      margin-bottom: 1px !important;*/
      background: #e4e5ee !important;
      margin: 0 !important;
      border-top-width: 0;
      border-left-width: 0;
      border-right-width: 0;
      border-bottom: 1px solid rgb(216, 216, 218);
      /*border-bottom: 1px solid #b3b3b3;*/
    }
  }
}
</style>